import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import api from '../lib/api';
import { toast } from 'sonner';
import { Upload as UploadIcon, Music, Image as ImageIcon, X, Film } from 'lucide-react';
import { motion } from 'framer-motion';

// Simple, single-POST upload — no chunks, no sessions, no resume state.
// This is the flow that worked reliably before we added chunking. Ingress
// caps around 500MB, so anything larger will 413 with a clear message.
async function uploadFile(file, kind, onProgress) {
  const endpoint = kind === 'audio' ? '/upload/audio'
    : kind === 'video' ? '/upload/video'
    : '/upload/extract-audio';
  const form = new FormData();
  form.append('file', file);
  const { data } = await api.post(endpoint, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 15 * 60 * 1000,
    onUploadProgress: (evt) => {
      if (onProgress && evt.total) onProgress(Math.round((evt.loaded / evt.total) * 100));
    },
  });
  return data;
}

const GENRES = [
  'House', 'Techno', 'Hip-Hop', 'R&B', 'EDM', 'Drum & Bass',
  'Trance', 'Dubstep', 'Lo-Fi', 'Ambient', 'Jazz', 'Funk',
  'Disco', 'Reggae', 'Afrobeat', 'Trap', 'Garage', 'Minimal'
];

const AUDIO_EXTS = ['mp3', 'wav', 'm4a', 'flac', 'ogg', 'aac'];
const VIDEO_EXTS = ['mp4', 'mov', 'webm', 'm4v', 'mkv', 'avi'];
const AUDIO_ACCEPT_EXTS = [...AUDIO_EXTS, ...VIDEO_EXTS];  // audio mode accepts video too (auto-extracts)
const AUDIO_MAX_DURATION_S = 3 * 60 * 60;  // 3-hour soft warning (long DJ sets)
const VIDEO_MAX_DURATION_S = 2 * 60 * 60;  // 2-hour soft warning
const AUDIO_MAX_SIZE_MB = 490;             // ~490 MB — nginx ingress caps single POSTs around 500 MB
const VIDEO_MAX_SIZE_MB = 490;             // ~490 MB

export const Upload = () => {
  const navigate = useNavigate();
  const [mediaType, setMediaType] = useState('audio'); // 'audio' | 'video'
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [genre, setGenre] = useState('House');
  const [tags, setTags] = useState('');
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaPath, setMediaPath] = useState(null);
  const [coverFile, setCoverFile] = useState(null);
  const [coverPath, setCoverPath] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [duration, setDuration] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadPercent, setUploadPercent] = useState(0);
  const [uploadError, setUploadError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const mediaRef = useRef(null);
  const coverInputRef = useRef(null);

  const resetMedia = () => {
    setMediaFile(null);
    setMediaPath(null);
    setDuration(0);
    setUploadError(null);
    setUploadPercent(0);
    if (mediaRef.current) mediaRef.current.value = '';
  };

  const handleMediaSelect = async (e) => {
    const file = e.target.files?.[0];
    // iOS Safari can hand us back nothing (Photos transcode drop) or a 0-byte
    // shell if the export failed. Both used to fail silently — now surface
    // them so the user sees what's happening instead of a dead form.
    if (!file) {
      toast.error("iPhone didn't finish preparing that video. Tap Upload again and give Photos a few seconds to export before selecting.");
      return;
    }
    if (file.size === 0) {
      toast.error(`"${file.name}" came through as 0 bytes — iPhone dropped the transcode. Try again, or use "Files" app instead of Photos.`);
      return;
    }
    // Immediately show the file card so the user knows the picker worked,
    // even before we know its duration or start uploading. Prevents the
    // "I clicked and nothing happened" pattern reported on iPhone Safari.
    setMediaFile(file);
    setUploadPercent(0);
    setUploadError(null);
    toast.info(`Got "${file.name}" (${(file.size / 1024 / 1024).toFixed(1)}MB) — uploading…`);

    const ext = (file.name.split('.').pop() || '').toLowerCase();
    const isVideoMode = mediaType === 'video';
    const allowed = isVideoMode ? VIDEO_EXTS : AUDIO_ACCEPT_EXTS;
    if (!allowed.includes(ext)) {
      toast.error(`Unsupported format: .${ext}. Allowed: ${allowed.join(', ')}`);
      resetMedia();
      return;
    }
    // Decide whether we'll be extracting audio from a video file
    const isVideoFile = VIDEO_EXTS.includes(ext);
    const extractingAudio = !isVideoMode && isVideoFile;
    const maxSizeMB = isVideoMode || extractingAudio ? VIDEO_MAX_SIZE_MB : AUDIO_MAX_SIZE_MB;
    const fileMB = file.size / 1024 / 1024;
    if (fileMB > maxSizeMB) {
      toast.error(`File too big (${fileMB.toFixed(1)}MB). Max is ${maxSizeMB}MB — try trimming it or exporting at a lower quality first.`);
      resetMedia();
      return;
    }

    // Read duration via HTML5 element (best-effort; some phone HEVC videos won't decode in-browser)
    const tempUrl = URL.createObjectURL(file);
    const el = isVideoFile ? document.createElement('video') : new Audio();
    el.preload = 'metadata';
    el.onloadedmetadata = () => {
      const dur = Math.floor(el.duration || 0);
      setDuration(dur);
      URL.revokeObjectURL(tempUrl);
      // Soft warning only — server will accept anyway
      const maxDur = isVideoMode ? VIDEO_MAX_DURATION_S : AUDIO_MAX_DURATION_S;
      if (dur > maxDur && !extractingAudio) {
        toast.warning(`Heads up — this is ${Math.floor(dur/60)} min long. Recommended max is ${maxDur/60} min.`);
      }
    };
    el.onerror = () => {
      // Can't read metadata (e.g. HEVC on non-Safari) — that's fine, we still upload
      URL.revokeObjectURL(tempUrl);
    };
    el.src = tempUrl;

    // Upload with progress
    setUploading(true);
    if (extractingAudio) {
      toast.info('Extracting audio from your video...');
    }
    try {
      const kind = isVideoMode ? 'video' : (extractingAudio ? 'extract-audio' : 'audio');
      const data = await uploadFile(file, kind, (pct) => setUploadPercent(pct));
      setMediaPath(data.path);
      setUploadPercent(100);
      const successMsg = data.extracted_from_video
        ? 'Audio extracted from video — ready to drop'
        : `${isVideoMode ? 'Video' : 'Audio'} uploaded — ready to drop`;
      toast.success(successMsg);
    } catch (err) {
      // Direct single-POST upload — failures are usually size (413) or a
      // real server error. Keep the file selected so Retry doesn't wipe it.
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      let msg = detail || err.message || 'Upload failed - please try again';
      if (status === 413 || /413/.test(msg)) msg = `File too big — max 490MB. Try trimming or re-exporting at a lower bitrate.`;
      else if (status === 502 || status === 504) msg = 'Server overloaded during upload. Try again in a minute.';
      else if (/Failed to fetch|NetworkError|TypeError|Network Error/i.test(msg)) msg = 'Connection dropped mid-upload. Tap Retry when you\'re back online.';
      setUploadError(msg);
      toast.error(msg);
      setMediaPath(null);
    } finally {
      setUploading(false);
    }
  };

  const retryUpload = async () => {
    if (!mediaFile) return;
    const ext = (mediaFile.name.split('.').pop() || '').toLowerCase();
    const isVideoMode = mediaType === 'video';
    const isVideoFile = VIDEO_EXTS.includes(ext);
    const extractingAudio = !isVideoMode && isVideoFile;
    setUploading(true);
    setUploadError(null);
    setUploadPercent(0);
    try {
      const kind = isVideoMode ? 'video' : (extractingAudio ? 'extract-audio' : 'audio');
      const data = await uploadFile(mediaFile, kind, (pct) => setUploadPercent(pct));
      setMediaPath(data.path);
      setUploadPercent(100);
      toast.success('Uploaded — ready to drop');
    } catch (err) {
      let msg = err.message || 'Upload failed';
      if (/Failed to fetch|NetworkError/i.test(msg)) msg = 'Still no signal. Try again when you\'re back online.';
      setUploadError(msg);
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  const handleCoverSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setCoverFile(file);
    setCoverPreview(URL.createObjectURL(file));
    try {
      const form = new FormData();
      form.append('file', file);
      const { data } = await api.post('/upload/image', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setCoverPath(data.path);
    } catch {
      toast.error('Cover upload failed');
      setCoverFile(null);
      setCoverPreview(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!mediaPath) {
      toast.error(`Please upload ${mediaType === 'video' ? 'a video' : 'an audio'} file first`);
      return;
    }
    setSubmitting(true);
    try {
      const tagArr = tags.split(',').map(t => t.trim().toLowerCase()).filter(Boolean);
      await api.post('/posts', {
        title,
        description,
        genre,
        tags: tagArr,
        audio_path: mediaPath,
        cover_path: coverPath,
        duration,
        media_type: mediaType,
      });
      toast.success('Mix dropped. Hype it up!');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Post failed');
    } finally {
      setSubmitting(false);
    }
  };

  const isVideoMode = mediaType === 'video';
  // iOS Photos picker occasionally drops the change event when accept mixes
  // MIME types and extensions — sticking to MIME types keeps the picker
  // reliable across iOS Safari, Android Chrome and desktop.
  const acceptStr = isVideoMode ? 'video/*' : 'audio/*,video/*';
  const durationStr = duration
    ? `${Math.floor(duration / 60)}:${(duration % 60).toString().padStart(2, '0')}`
    : '—';

  return (
    <Layout>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="mb-8"
      >
        <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#FFB000] mb-2">Upload</div>
        <h1 className="font-heading font-black text-white text-4xl md:text-5xl tracking-tighter">
          Drop a new {isVideoMode ? 'video' : 'mix'}
        </h1>
      </motion.div>

      <form onSubmit={handleSubmit} className="post-card p-6 md:p-8 space-y-6">
        {/* Media type toggle */}
        <div>
          <label className="text-xs tracking-[0.2em] uppercase font-bold text-[#A1A1AA] mb-3 block">
            What are you posting?
          </label>
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => { setMediaType('audio'); resetMedia(); }}
              className={`p-4 rounded-2xl border-2 flex items-center gap-3 transition-all ${
                !isVideoMode
                  ? 'border-[#FFB000] bg-[#FFB000]/10 text-white'
                  : 'border-white/10 hover:border-white/20 text-[#A1A1AA]'
              }`}
              data-testid="mediatype-audio"
            >
              <Music size={20} className={!isVideoMode ? 'text-[#FFB000]' : ''} />
              <div className="text-left">
                <div className="font-bold font-heading text-sm">Audio mix</div>
                <div className="text-xs opacity-70">Up to 490MB · MP3/WAV/FLAC — or drop a video &amp; we&apos;ll pull the audio</div>
              </div>
            </button>
            <button
              type="button"
              onClick={() => { setMediaType('video'); resetMedia(); }}
              className={`p-4 rounded-2xl border-2 flex items-center gap-3 transition-all ${
                isVideoMode
                  ? 'border-[#FFB000] bg-[#FFB000]/10 text-white'
                  : 'border-white/10 hover:border-white/20 text-[#A1A1AA]'
              }`}
              data-testid="mediatype-video"
            >
              <Film size={20} className={isVideoMode ? 'text-[#FFB000]' : ''} />
              <div className="text-left">
                <div className="font-bold font-heading text-sm">Video</div>
                <div className="text-xs opacity-70">Up to 490MB · MP4/MOV/WebM</div>
              </div>
            </button>
          </div>
        </div>

        {/* Media Upload */}
        <div>
          <label className="text-xs tracking-[0.2em] uppercase font-bold text-[#A1A1AA] mb-3 block">
            {isVideoMode ? 'Video file' : 'Audio file'}
          </label>
          {!mediaFile ? (
            <label className="block cursor-pointer relative">
              <input
                ref={mediaRef}
                type="file"
                accept={acceptStr}
                onChange={handleMediaSelect}
                // iOS Safari silently drops the `change` event when a file
                // input is `display:none` inside a <label>. Use opacity-0 +
                // position:absolute instead so the element stays in flow and
                // still receives events after the Photos picker closes.
                className="absolute inset-0 opacity-0 w-full h-full cursor-pointer"
                onClick={(e) => { e.currentTarget.value = ''; }}
                data-testid="upload-audio-input"
              />
              <div className="border-2 border-dashed border-white/10 rounded-2xl p-10 text-center hover:border-[#FFB000]/40 hover:bg-[#FFB000]/5 transition-all">
                {isVideoMode ? (
                  <Film size={32} className="text-[#FFB000] mx-auto mb-3" />
                ) : (
                  <Music size={32} className="text-[#FFB000] mx-auto mb-3" />
                )}
                <div className="text-white font-bold font-heading mb-1">
                  Tap to choose {isVideoMode ? 'video' : 'audio or video'}
                </div>
                <div className="text-[#A1A1AA] text-sm">
                  {isVideoMode
                    ? 'Up to 490MB · MP4/MOV/WebM'
                    : 'Up to 490MB · MP3/WAV/FLAC — or drop a video & we\u2019ll pull the audio'}
                </div>
              </div>
            </label>
          ) : (
            <div className="border border-white/10 rounded-2xl p-5">
              <div className="flex items-center gap-4 mb-3">
                {isVideoMode
                  ? <Film size={24} className={mediaPath ? 'text-[#FFB000]' : uploading ? 'text-[#A1A1AA] animate-pulse' : 'text-[#EF4444]'} />
                  : <Music size={24} className={mediaPath ? 'text-[#FFB000]' : uploading ? 'text-[#A1A1AA] animate-pulse' : 'text-[#EF4444]'} />
                }
                <div className="flex-1 min-w-0">
                  <div className="text-white font-bold truncate font-heading text-sm">{mediaFile.name}</div>
                  <div className={`text-xs ${mediaPath ? 'text-[#FFB000]' : uploading ? 'text-[#A1A1AA]' : 'text-[#EF4444]'}`}>
                    {(mediaFile.size / 1024 / 1024).toFixed(1)} MB · {durationStr}
                    {uploading && ` · Uploading ${uploadPercent}%`}
                    {mediaPath && !uploading && ' · ✓ Uploaded'}
                    {!mediaPath && !uploading && uploadError && ' · Failed'}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={resetMedia}
                  className="text-[#A1A1AA] hover:text-[#EF4444] p-2"
                  data-testid="remove-audio"
                >
                  <X size={18} />
                </button>
              </div>
              {/* Progress bar */}
              {(uploading || mediaPath) && (
                <div className="w-full h-2 rounded-full bg-white/5 overflow-hidden" data-testid="upload-progress">
                  <div
                    className="h-full bg-gradient-to-r from-[#FFB000] to-[#FFD147] transition-all duration-200"
                    style={{ width: `${uploadPercent}%` }}
                  />
                </div>
              )}
            </div>
          )}
          {uploadError && (
            <div className="mt-3 p-3 rounded-xl bg-[#EF4444]/10 border border-[#EF4444]/30 text-[#EF4444] text-sm" data-testid="upload-error">
              <div className="mb-2"><strong>Upload failed:</strong> {uploadError}</div>
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={retryUpload}
                  disabled={uploading}
                  className="btn-pill btn-primary text-xs disabled:opacity-50"
                  data-testid="upload-retry"
                >
                  {uploading ? `Retrying ${uploadPercent}%…` : 'Retry upload'}
                </button>
                <span className="text-xs text-[#A1A1AA]">
                  Your file's still selected — no need to pick it again.
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Cover */}
        <div>
          <label className="text-xs tracking-[0.2em] uppercase font-bold text-[#A1A1AA] mb-3 block">
            Cover art (optional)
          </label>
          <div className="flex gap-4 items-center">
            {coverPreview ? (
              <img src={coverPreview} alt="" className="w-24 h-24 rounded-xl object-cover" />
            ) : (
              <div className="w-24 h-24 rounded-xl bg-[#1A1A1D] border border-white/10 flex items-center justify-center">
                <ImageIcon size={24} className="text-[#A1A1AA]" />
              </div>
            )}
            <div>
              <input
                ref={coverInputRef}
                type="file"
                accept="image/*"
                onChange={handleCoverSelect}
                onClick={(e) => { e.currentTarget.value = ''; }}
                className="absolute opacity-0 w-0 h-0 overflow-hidden"
                data-testid="upload-cover-input"
              />
              <button
                type="button"
                onClick={() => coverInputRef.current?.click()}
                className="btn-pill btn-outline text-sm"
                data-testid="upload-cover-btn"
              >
                {coverPath ? 'Change cover' : 'Choose cover'}
              </button>
            </div>
          </div>
        </div>

        {/* Title */}
        <div>
          <label className="text-xs tracking-[0.2em] uppercase font-bold text-[#A1A1AA] mb-3 block">Title</label>
          <input
            required
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder={isVideoMode ? 'e.g. Studio session — deep set' : 'e.g. Sunrise Set @ Panorama Bar'}
            className="input-default"
            data-testid="upload-title"
          />
        </div>

        {/* Description */}
        <div>
          <label className="text-xs tracking-[0.2em] uppercase font-bold text-[#A1A1AA] mb-3 block">Description</label>
          <textarea
            rows={3}
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="Tell the story behind this..."
            className="input-default resize-none"
            data-testid="upload-description"
          />
        </div>

        {/* Genre */}
        <div>
          <label className="text-xs tracking-[0.2em] uppercase font-bold text-[#A1A1AA] mb-3 block">Genre</label>
          <div className="flex flex-wrap gap-2">
            {GENRES.map(g => (
              <button
                key={g}
                type="button"
                onClick={() => setGenre(g)}
                className={`px-4 py-2 rounded-full text-sm font-bold font-heading transition-all ${
                  genre === g
                    ? 'bg-[#FFB000] text-black'
                    : 'bg-[#1A1A1D] text-[#A1A1AA] hover:text-white border border-white/5'
                }`}
                data-testid={`upload-genre-${g}`}
              >
                {g}
              </button>
            ))}
          </div>
        </div>

        {/* Tags */}
        <div>
          <label className="text-xs tracking-[0.2em] uppercase font-bold text-[#A1A1AA] mb-3 block">Tags (comma separated)</label>
          <input
            value={tags}
            onChange={e => setTags(e.target.value)}
            placeholder="deep, 3am, afterhours"
            className="input-default"
            data-testid="upload-tags"
          />
        </div>

        <div className="flex flex-col gap-3">
          {!mediaPath && !uploading && (
            <div className="text-xs text-[#A1A1AA] text-center">
              👆 You need to upload {isVideoMode ? 'a video' : 'an audio'} file before you can drop it
            </div>
          )}
          {uploading && (
            <div className="text-xs text-[#FFB000] text-center animate-pulse">
              Uploading {uploadPercent}% — please wait, don&apos;t close this tab
            </div>
          )}
          <div className="flex gap-3">
            <button
              type="submit"
              disabled={submitting || uploading || !mediaPath}
              className="btn-pill btn-primary flex-1 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="upload-submit"
            >
              <UploadIcon size={16} />
              {submitting ? 'Dropping...' : uploading ? `Uploading ${uploadPercent}%` : !mediaPath ? `Add ${isVideoMode ? 'video' : 'audio'} first` : 'Drop it'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              className="btn-pill btn-outline"
              data-testid="upload-cancel"
            >
              Cancel
            </button>
          </div>
        </div>
      </form>
    </Layout>
  );
};

export default Upload;
